import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getTextField } from '@lender/utils';
import { Button, Typography } from '@mui/material';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropType from 'prop-types';
import { object } from 'yup';

import useForm from 'hooks/useForm';
import useLoading from 'hooks/useLoading';

import { showMessageAction } from 'store/global/actions';
import { updateCallNotesAction } from 'store/loanApplications/actions';
import { selectFormLockedFieldsMap, selectFormLockedPage } from 'store/loanApplications/selectors';
import { getDebouncedSearchValue } from 'store/search/selectors';

import { ActivitiesTestIds } from '../../../Activities.testIds';
import HighlightText from '../../../HighlightText';

const callNotesSchema = object().shape({
  callNotes: getTextField({ isRequired: false }).nullable(),
});

const CallNotes = ({ callHistoryId, callNotes = '' }) => {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useLoading(false);

  const highlightText = useSelector(getDebouncedSearchValue);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.ACTIVITIES_CALLS));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.ACTIVITIES_CALLS));

  const isDisabledByLock = isLoading || isPageLocked;

  const [isEditMode, setIsEditMode] = useState(false);
  const isViewMode = !isEditMode;

  const { form, isFormChanged, setInitialFormValues, setSchema, setInForm, handleChange } = useForm(
    { callNotes },
  );

  useEffect(() => {
    setSchema(callNotesSchema);
  }, [setSchema]);

  const onToggleEditMode = useCallback(() => {
    setIsEditMode(prevIsEdit => !prevIsEdit);
    setInForm('callNotes', callNotes);
  }, [callNotes, setInForm]);

  const onSubmit = useCallback(
    e => {
      e.preventDefault();
      setIsLoading(true);

      dispatch(updateCallNotesAction({ callHistoryId, callNotes: form.callNotes.trim() }))
        .then(() => {
          setIsEditMode(false);
          setInitialFormValues(form);
          dispatch(showMessageAction({ message: 'Call Notes updated', variant: 'success' }));
        })
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [setIsLoading, dispatch, callHistoryId, form, setInitialFormValues],
  );

  return (
    <form noValidate className="flex flex-col px-10 py-16 space-y-10" onSubmit={onSubmit}>
      <div className="flex justify-between items-center">
        <Typography variant="regular14">Call Notes</Typography>

        {isEditMode && (
          <div className="flex space-x-2">
            <Button
              data-testid={ActivitiesTestIds.CALL_NOTES_SAVE_BUTTON}
              disabled={!isFormChanged || isLoading}
              size="small"
              type="submit"
              variant="contained"
            >
              Save
            </Button>
            <Button
              data-testid={ActivitiesTestIds.CALL_NOTES_CANCEL_BUTTON}
              disabled={isLoading}
              size="small"
              type="button"
              variant="contained"
              onClick={onToggleEditMode}
            >
              Cancel
            </Button>
          </div>
        )}

        {isViewMode && (
          <Button
            data-testid={ActivitiesTestIds.CALL_NOTES_EDIT_BUTTON}
            disabled={isLoading}
            size="small"
            type="button"
            variant="contained"
            onClick={onToggleEditMode}
          >
            {callNotes ? 'Edit' : 'Add'}
          </Button>
        )}
      </div>

      {isEditMode && (
        <TextFieldCDS
          fullWidth
          multiline
          className="mb-24"
          dataTestId={ActivitiesTestIds.CALL_NOTES_INPUT}
          disabled={isDisabledByLock || lockedFieldsMap.callNotes}
          id={`callNotes-${callHistoryId}`}
          name="callNotes"
          rows={5}
          type="text"
          value={form.callNotes}
          variant="outlined"
          onChange={handleChange}
        />
      )}

      {isViewMode && !!callNotes && (
        <div className="whitespace-pre-line">
          <HighlightText highlight={highlightText} text={callNotes} />
        </div>
      )}
    </form>
  );
};

CallNotes.propTypes = {
  callHistoryId: PropType.string.isRequired,
  callNotes: PropType.string,
};

export default memo(CallNotes);
